import { useState, useEffect } from "react";
import { admin } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Users, Mail, DollarSign, UserPlus, TrendingUp,
  Activity, AlertTriangle, CheckCircle,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";

function StatCard({ label, value, icon: Icon, accent, sub }: {
  label: string; value: string | number; icon: React.ElementType; accent: string; sub?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      <div className={`inline-flex p-2.5 rounded-xl mb-3 ${accent}`}>
        <Icon size={17} />
      </div>
      <p className="text-2xl font-bold text-ink leading-none">{value}</p>
      <p className="text-xs font-semibold text-slate uppercase tracking-wider mt-1.5">{label}</p>
      {sub && <p className="text-xs text-slate/70 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminOverviewPage() {
  const [data, setData]   = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    admin.analytics().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner className="h-screen" />;

  const u    = data?.users        || {};
  const s    = data?.subscribers  || {};
  const e    = data?.emails       || {};
  const sent = parseInt(e.total_sent)   || 0;
  const opens= parseInt(e.total_opens)  || 0;
  const clicks=parseInt(e.total_clicks) || 0;

  const totalUsers   = parseInt(u.total_users)   || 0;
  const activeUsers  = parseInt(u.active_users)  || 0;
  const mrr          = data?.mrr || 0;
  const new30d       = parseInt(u.new_users_30d) || 0;

  // Dynamic plan distribution from API
  const PLAN_COLORS: Record<string, string> = {
    starter: "#CBD5E1", growth: "#FF5F45", enterprise: "#0A8F7F",
    professional: "#6366F1", business: "#F59E0B", scale: "#8B5CF6",
  };
  const planData = (data?.planCounts || []).map((pc: any) => ({
    name: pc.name,
    count: parseInt(pc.user_count) || 0,
    color: PLAN_COLORS[pc.slug] || "#94A3B8",
  }));

  const rateData = [
    { name: "Open rate",   value: sent > 0 ? parseFloat(((opens  / sent) * 100).toFixed(1)) : 0 },
    { name: "Click rate",  value: sent > 0 ? parseFloat(((clicks / sent) * 100).toFixed(1)) : 0 },
    { name: "Bounce rate", value: sent > 0 ? parseFloat(((parseInt(e.total_bounces || "0") / sent) * 100).toFixed(1)) : 0 },
  ];

  // System health checks
  const health = [
    { label: "SMTP server",        ok: true,  note: "Connected" },
    { label: "IMAP poller",        ok: true,  note: "Running"   },
    { label: "Automation engine",  ok: true,  note: "Active"    },
    { label: "Blacklist scheduler",ok: true,  note: "Scheduled" },
    { label: "Warmup scheduler",   ok: true,  note: "Running"   },
  ];

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">System Overview</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">Platform-wide metrics and health</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total Users"    value={totalUsers.toLocaleString()}   icon={Users}     accent="bg-blue-50 text-blue-600"   sub={`${new30d} new this month`} />
          <StatCard label="Active Users"   value={activeUsers.toLocaleString()}  icon={UserPlus}  accent="bg-teal/10 text-teal"        sub={`${totalUsers > 0 ? Math.round((activeUsers/totalUsers)*100) : 0}% of total`} />
          <StatCard label="Emails Sent"    value={sent.toLocaleString()}          icon={Mail}      accent="bg-coral/10 text-coral"      sub="All time" />
          <StatCard label="Est. MRR"       value={`$${mrr.toLocaleString()}`}     icon={DollarSign}accent="bg-green-50 text-green-600" sub="Monthly recurring" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">

          {/* Plan distribution */}
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
            <h2 className="font-bold text-ink mb-5">Plan distribution</h2>
            <div className="space-y-4">
              {planData.map((p) => {
                const pct = totalUsers > 0 ? Math.round((p.count / totalUsers) * 100) : 0;
                return (
                  <div key={p.name}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-ink">{p.name}</span>
                      <span className="text-slate">{p.count.toLocaleString()} <span className="text-slate/60 text-xs">({pct}%)</span></span>
                    </div>
                    <div className="h-2.5 bg-black/[0.04] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: p.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { label: "Subscribers total", value: (parseInt(s.total_subscribers) || 0).toLocaleString() },
                { label: "New users (30d)",   value: new30d },
                { label: "Total campaigns",   value: parseInt(data?.campaigns?.total_campaigns) || 0 },
              ].map(({ label, value }) => (
                <div key={label} className="bg-black/[0.02] rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-ink">{value}</p>
                  <p className="text-[10px] text-slate font-semibold uppercase tracking-wider mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Email performance */}
          <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
            <h2 className="font-bold text-ink mb-5">Email performance</h2>
            {sent > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={rateData} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#5A6872" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#0B1215" }} axisLine={false} tickLine={false} width={80} />
                    <Tooltip formatter={(v: number) => [`${v}%`]} contentStyle={{ background: "#0B1215", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#FF5F45" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {[
                    { label: "Total opens",  value: opens.toLocaleString()  },
                    { label: "Total clicks", value: clicks.toLocaleString() },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-black/[0.02] rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-ink">{value}</p>
                      <p className="text-[10px] text-slate font-semibold uppercase tracking-wider mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-slate gap-2">
                <TrendingUp size={24} className="opacity-20" />
                <p className="text-sm">No email data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* System health */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-ink">System health</h2>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
              <Activity size={13} /> All systems operational
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {health.map((h) => (
              <div key={h.label}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs ${
                  h.ok ? "bg-green-50 border-green-100 text-green-800" : "bg-red-50 border-red-100 text-red-800"
                }`}>
                {h.ok
                  ? <CheckCircle size={13} className="text-green-600 flex-shrink-0" />
                  : <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
                }
                <div className="min-w-0">
                  <p className="font-semibold truncate">{h.label}</p>
                  <p className="opacity-70">{h.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
